/**
 * 3PL routes. Every record has its own URL so refresh, Back/Forward and
 * shared links work:
 *   public   /fulfillment/request-quote, /fulfillment/pricing, /fulfillment/quote/:token
 *   merchant /merchant/...
 *   admin    /ops/...
 *   floor    /floor/...
 */
import React, { lazy } from 'react';
import { Navigate, Route } from 'react-router-dom';
import ProtectedRoute from '../components/ProtectedRoute';
import { admin } from './api';
import { FlProvider } from './context';
import { useFetch } from './hooks';
import { AdminShell, FloorShell, MerchantShell } from './shells';

const P = (load) => lazy(load);

// Public
const RequestQuote = P(() => import('./public/RequestQuote'));
const QuoteConfirmation = P(() => import('./public/QuoteConfirmation'));
const PricingExplainer = P(() => import('./public/PricingExplainer'));
const QuoteView = P(() => import('./public/QuoteView'));
// Merchant
const MOverview = P(() => import('./merchant/Overview'));
const MOnboarding = P(() => import('./merchant/Onboarding'));
const MProducts = P(() => import('./merchant/Products'));
const MProductEditor = P(() => import('./merchant/ProductEditor'));
const MProductImport = P(() => import('./merchant/ProductImport'));
const MInventory = P(() => import('./merchant/Inventory'));
const MSkuDetail = P(() => import('./merchant/SkuDetail'));
const MInbound = P(() => import('./merchant/Inbound'));
const MAsnWizard = P(() => import('./merchant/AsnWizard'));
const MAsnImport = P(() => import('./merchant/AsnImport'));
const MAsnDetail = P(() => import('./merchant/AsnDetail'));
const MOrders = P(() => import('./merchant/Orders'));
const MOrderNew = P(() => import('./merchant/OrderNew'));
const MOrderImport = P(() => import('./merchant/OrderImport'));
const MOrderDetail = P(() => import('./merchant/OrderDetail'));
const MShipments = P(() => import('./merchant/Shipments'));
const MShipmentDetail = P(() => import('./merchant/ShipmentDetail'));
const MReturns = P(() => import('./merchant/Returns'));
const MReturnNew = P(() => import('./merchant/ReturnNew'));
const MReturnDetail = P(() => import('./merchant/ReturnDetail'));
const MBilling = P(() => import('./merchant/Billing'));
const MChargeDetail = P(() => import('./merchant/ChargeDetail'));
const MInvoiceDetail = P(() => import('./merchant/InvoiceDetail'));
const MReports = P(() => import('./merchant/Reports'));
const MConnections = P(() => import('./merchant/Connections'));
const MSupport = P(() => import('./merchant/Support'));
const MSettings = P(() => import('./merchant/Settings'));
// Admin
const ATower = P(() => import('./admin/Tower'));
const AEnquiries = P(() => import('./admin/Enquiries'));
const AEnquiryDetail = P(() => import('./admin/EnquiryDetail'));
const AMerchants = P(() => import('./admin/Merchants'));
const AMerchantDetail = P(() => import('./admin/MerchantDetail'));
const AInbound = P(() => import('./admin/Inbound'));
const AInboundDetail = P(() => import('./admin/InboundDetail'));
const AInventory = P(() => import('./admin/Inventory'));
const AOrders = P(() => import('./admin/Orders'));
const AOrderDetail = P(() => import('./admin/OrderDetail'));
const AShipments = P(() => import('./admin/Shipments'));
const AReturns = P(() => import('./admin/Returns'));
const AExceptions = P(() => import('./admin/Exceptions'));
const ABilling = P(() => import('./admin/Billing'));
const AReports = P(() => import('./admin/Reports'));
const AConfig = P(() => import('./admin/Config'));
const ATraining = P(() => import('./admin/Training'));
// Floor
const FTasks = P(() => import('./floor/Tasks'));
const FReceive = P(() => import('./floor/Receive'));
const FPutaway = P(() => import('./floor/Putaway'));
const FPick = P(() => import('./floor/Pick'));
const FPack = P(() => import('./floor/Pack'));
const FDispatch = P(() => import('./floor/Dispatch'));
const FReturnInspect = P(() => import('./floor/ReturnInspect'));
const FCount = P(() => import('./floor/Count'));
const FTransfer = P(() => import('./floor/Transfer'));
const FPutBack = P(() => import('./floor/PutBack'));
const FUnitTask = P(() => import('./floor/UnitTask'));

const Guard = ({ children }) => (
  <ProtectedRoute><FlProvider>{children}</FlProvider></ProtectedRoute>
);

function AdminRoot() {
  const ms = useFetch(() => admin.merchants().catch(() => ({ items: [] })), []);
  return <AdminShell merchantsForContext={ms.data?.items || []} />;
}

/** Public 3PL routes (no login, except accepting a quote). */
export const flPublicRoutes = [
  <Route key="flq" path="/fulfillment/request-quote" element={<RequestQuote />} />,
  <Route key="flqc" path="/fulfillment/request-quote/confirmation" element={<QuoteConfirmation />} />,
  <Route key="flp" path="/fulfillment/pricing" element={<PricingExplainer />} />,
  <Route key="flqv" path="/fulfillment/quote/:token" element={<QuoteView />} />,
];

export const flAppRoutes = [
  <Route key="flm" path="/merchant" element={<Guard><MerchantShell /></Guard>}>
    <Route index element={<MOverview />} />
    <Route path="onboarding" element={<MOnboarding />} />
    <Route path="products" element={<MProducts />} />
    <Route path="products/new" element={<MProductEditor />} />
    <Route path="products/import" element={<MProductImport />} />
    <Route path="products/:skuId" element={<MProductEditor />} />
    <Route path="inventory" element={<MInventory />} />
    <Route path="inventory/:skuId" element={<MSkuDetail />} />
    <Route path="inbound" element={<MInbound />} />
    <Route path="inbound/new" element={<MAsnWizard />} />
    <Route path="inbound/import" element={<MAsnImport />} />
    <Route path="inbound/:asnId" element={<MAsnDetail />} />
    <Route path="orders" element={<MOrders />} />
    <Route path="orders/new" element={<MOrderNew />} />
    <Route path="orders/import" element={<MOrderImport />} />
    <Route path="orders/:orderId" element={<MOrderDetail />} />
    <Route path="shipments" element={<MShipments />} />
    <Route path="shipments/:shipmentId" element={<MShipmentDetail />} />
    <Route path="returns" element={<MReturns />} />
    <Route path="returns/new" element={<MReturnNew />} />
    <Route path="returns/:rmaId" element={<MReturnDetail />} />
    <Route path="billing" element={<MBilling />} />
    <Route path="billing/charges/:chargeId" element={<MChargeDetail />} />
    <Route path="billing/invoices/:invoiceId" element={<MInvoiceDetail />} />
    <Route path="reports" element={<MReports />} />
    <Route path="connections" element={<MConnections />} />
    <Route path="support" element={<MSupport />} />
    <Route path="settings" element={<MSettings />} />
    <Route path="*" element={<Navigate to="/merchant" replace />} />
  </Route>,
  <Route key="flo" path="/ops" element={<Guard><AdminRoot /></Guard>}>
    <Route index element={<ATower />} />
    <Route path="enquiries" element={<AEnquiries />} />
    <Route path="enquiries/:enquiryId" element={<AEnquiryDetail />} />
    <Route path="merchants" element={<AMerchants />} />
    <Route path="merchants/:merchantId" element={<AMerchantDetail />} />
    <Route path="inbound" element={<AInbound />} />
    <Route path="inbound/:asnId" element={<AInboundDetail />} />
    <Route path="inventory" element={<AInventory />} />
    <Route path="orders" element={<AOrders />} />
    <Route path="orders/:orderId" element={<AOrderDetail />} />
    <Route path="shipments" element={<AShipments />} />
    <Route path="returns" element={<AReturns />} />
    <Route path="exceptions" element={<AExceptions />} />
    <Route path="billing" element={<ABilling />} />
    <Route path="reports" element={<AReports />} />
    <Route path="config" element={<AConfig />} />
    <Route path="training" element={<ATraining />} />
    <Route path="*" element={<Navigate to="/ops" replace />} />
  </Route>,
  <Route key="flf" path="/floor" element={<Guard><FloorShell /></Guard>}>
    <Route index element={<FTasks />} />
    <Route path="receive" element={<FReceive />} />
    <Route path="receive/:asnId" element={<FReceive />} />
    <Route path="putaway/:taskId" element={<FPutaway />} />
    <Route path="pick/:taskId" element={<FPick />} />
    <Route path="pack/:taskId" element={<FPack />} />
    <Route path="dispatch/:taskId" element={<FDispatch />} />
    <Route path="returns" element={<FReturnInspect />} />
    <Route path="returns/:rmaId" element={<FReturnInspect />} />
    <Route path="count/:taskId" element={<FCount />} />
    <Route path="transfer/:taskId" element={<FTransfer />} />
    <Route path="putback/:taskId" element={<FPutBack />} />
    <Route path="unit/:taskId" element={<FUnitTask />} />
    <Route path="*" element={<Navigate to="/floor" replace />} />
  </Route>,
];

/** Where a floor task opens. */
export const FLOOR_TASK_PATH = {
  receive: (t) => `/floor/receive/${t.ref_id}`,
  putaway: (t) => `/floor/putaway/${t.task_id}`,
  pick: (t) => `/floor/pick/${t.task_id}`,
  pack: (t) => `/floor/pack/${t.task_id}`,
  dispatch: (t) => `/floor/dispatch/${t.task_id}`,
  putback: (t) => `/floor/putback/${t.task_id}`,
  count: (t) => `/floor/count/${t.task_id}`,
  transfer: (t) => `/floor/transfer/${t.task_id}`,
  return: (t) => `/floor/returns/${t.ref_id}`,
  restock: (t) => `/floor/unit/${t.task_id}`,
  rtm: (t) => `/floor/unit/${t.task_id}`,
  disposal: (t) => `/floor/unit/${t.task_id}`,
};
